/* Subject-based popular books grid powered by OpenLibrary. */

import { useState, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Loader2, BookOpen } from "lucide-react";
import type { PopularBook } from "@/types/bookDiscover";

const SUBJECT_OPTIONS = [
  { value: "fiction", label: "Fiction" },
  { value: "science_fiction", label: "Sci-Fi" },
  { value: "fantasy", label: "Fantasy" },
  { value: "mystery", label: "Mystery" },
  { value: "romance", label: "Romance" },
  { value: "biography", label: "Biography" },
  { value: "history", label: "History" },
  { value: "science", label: "Science" },
  { value: "philosophy", label: "Philosophy" },
  { value: "business", label: "Business" },
  { value: "self-help", label: "Self-Help" },
  { value: "psychology", label: "Psychology" },
  { value: "technology", label: "Technology" },
  { value: "cooking", label: "Cooking" },
  { value: "art", label: "Art" },
] as const;

interface BookBrowsePopularProps {
  onSelectBook: (book: PopularBook) => void;
}

export function BookBrowsePopular({ onSelectBook }: BookBrowsePopularProps) {
  const [books, setBooks] = useState<PopularBook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubject, setActiveSubject] = useState<string | null>(null);

  const fetchPopular = useAction(api.bookDiscover.fetchPopularBooks);

  const loadSubject = useCallback(
    async (subject: string) => {
      setActiveSubject(subject);
      setIsLoading(true);
      try {
        const data = await fetchPopular({ subject });
        setBooks(data);
      } catch {
        setBooks([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPopular],
  );

  function handleSubjectClick(subject: string) {
    if (activeSubject === subject) return;
    loadSubject(subject);
  }

  return (
    <div className="space-y-3">
      {/* Subject pill filter bar */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {SUBJECT_OPTIONS.map((s) => (
          <button
            key={s.value}
            type="button"
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
              activeSubject === s.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            onClick={() => handleSubjectClick(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Prompt to select subject */}
      {!activeSubject && !isLoading && (
        <p className="text-xs text-muted-foreground text-center py-3">
          Select a subject to browse popular books.
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Loading popular books...
          </span>
        </div>
      )}

      {/* Books grid */}
      {!isLoading && books.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {books.map((book) => (
            <button
              key={book.key}
              type="button"
              className="flex flex-col items-center gap-1 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSelectBook(book)}
            >
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  className="w-12 h-[72px] rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-12 h-[72px] rounded-sm bg-muted flex items-center justify-center">
                  <BookOpen className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {book.title}
              </span>
              <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                {book.author}
                {book.year ? ` (${book.year})` : ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activeSubject && books.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">
          No results found for this subject.
        </p>
      )}
    </div>
  );
}
